import {
  type ChartListSourceData,
  buildChartGa4Items,
  buildChartListIdentity,
} from '@/components/ChartProductList/chartListTracking';
import type { RailItem } from '@/components/InterruptionTile/types';
import { useTrackItemList } from '@/hooks/useTrackItemList';
import { useMemo, useRef } from 'react';

interface ChartListTrackerData extends ChartListSourceData {
  items: RailItem[];
}

/**
 * Tracking-only counterpart to `ChartProductList` for List view, where the
 * chart page renders its products server-side (`ProductListContentArea`) and
 * no React component is otherwise mounted, so `view_item_list` would
 * otherwise never fire.
 *
 * List view is server-rendered in one go with no pagination, so — unlike
 * `ChartProductList`, which reports only the newest page — this reports the
 * FULL item list starting at index 0, with no signature (the list cannot
 * change without a full page load).
 */
export const ChartListTracker = ({ data }: { data: ChartListTrackerData }) => {
  const items = (data.items ?? []) as RailItem[];
  const ref = useRef<HTMLDivElement>(null);

  const list = useMemo(
    () => buildChartListIdentity({ listName: data.listName, contentId: data.contentId }),
    [data.listName, data.contentId],
  );

  const ga4Items = useMemo(
    () => buildChartGa4Items(items, list, 0),
    [items, list],
  );

  useTrackItemList(ref, list, ga4Items);

  // Must occupy the position of the product list in the document so the
  // in-view observer is meaningful — a `display:none`/`hidden` element would
  // never be reported as visible and would never fire.
  return <div ref={ref} style={{ height: 0 }} />;
};
